import { DEFAULT_TAXONOMY_ITEMS, TaxonomyItem } from './defaultTaxonomyData';

export type { TaxonomyItem };

const STORAGE_KEY = 'qms_organization_taxonomy';

class TaxonomyService {
  private items: TaxonomyItem[] = [];
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.loadFromStorage();
    this.fetchFromDatabase().catch(() => {});
  }

  public async fetchFromDatabase(): Promise<TaxonomyItem[]> {
    try {
      if (typeof window !== 'undefined' && typeof fetch !== 'undefined') {
        const res = await fetch('/api/v1/admin/taxonomy', {
          headers: {
            'x-organization-id': 'ORG-XYZ',
            'Content-Type': 'application/json'
          }
        });
        if (res.ok) {
          const json = await res.json();
          if (json && json.success && Array.isArray(json.data) && json.data.length > 0) {
            this.items = json.data.map((r: any) => ({
              id: r.id ? String(r.id) : undefined,
              area: String(r.area).trim(),
              kind: String(r.kind).trim(),
              type: String(r.type).trim(),
              classification: String(r.classification).trim()
            }));
            this.persistAndNotify();
            return this.items;
          }
        }
      }
    } catch (err) {
      console.warn('[TaxonomyService] Database fetch fallback to local cache:', err);
    }
    return this.items;
  }

  private loadFromStorage(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            this.items = parsed;
            return;
          }
        }
      }
    } catch (err) {
      console.warn('Failed to load custom taxonomy from localStorage, using default:', err);
    }
    this.items = [...DEFAULT_TAXONOMY_ITEMS];
  }

  private persistAndNotify(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      }
    } catch (err) {
      console.warn('Failed to persist taxonomy to localStorage:', err);
    }
    this.notify();
  }

  private notify(): void {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch (err) {
        console.error('Error in taxonomy listener:', err);
      }
    });
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public getTaxonomy(): TaxonomyItem[] {
    return [...this.items];
  }

  public setTaxonomy(newItems: TaxonomyItem[]): void {
    this.items = [...newItems];
    this.persistAndNotify();
  }

  public resetTaxonomy(): void {
    this.items = [...DEFAULT_TAXONOMY_ITEMS];
    this.persistAndNotify();

    if (typeof window !== 'undefined' && typeof fetch !== 'undefined') {
      fetch('/api/v1/admin/taxonomy/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-organization-id': 'ORG-XYZ',
          'x-user-role': 'ROLE_SYSTEM_ADMIN'
        }
      }).catch(() => {});
    }
  }

  public getSummaryCounts(): {
    areasCount: number;
    kindsCount: number;
    typesCount: number;
    classificationsCount: number;
    kindsList: string[];
  } {
    const areas = new Set<string>();
    const kinds = new Set<string>();
    const types = new Set<string>();

    this.items.forEach((item) => {
      if (item.area) areas.add(item.area);
      if (item.kind) kinds.add(item.kind);
      if (item.type) types.add(item.type);
    });

    return {
      areasCount: areas.size,
      kindsCount: kinds.size,
      typesCount: types.size,
      classificationsCount: this.items.length,
      kindsList: Array.from(kinds)
    };
  }

  public getAreas(): { name: string; count: number }[] {
    const map = new Map<string, number>();
    this.items.forEach((item) => {
      if (!item.area) return;
      map.set(item.area, (map.get(item.area) || 0) + 1);
    });

    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }

  public getKindsForArea(area: string): { name: string; count: number }[] {
    const map = new Map<string, number>();
    this.items.forEach((item) => {
      if (item.area !== area || !item.kind) return;
      map.set(item.kind, (map.get(item.kind) || 0) + 1);
    });

    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }

  public getTypesForAreaAndKind(area: string, kind: string): { name: string; count: number }[] {
    const map = new Map<string, number>();
    this.items.forEach((item) => {
      if (item.area !== area || item.kind !== kind || !item.type) return;
      map.set(item.type, (map.get(item.type) || 0) + 1);
    });

    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  public getClassifications(area: string, kind: string, type: string): string[] {
    const set = new Set<string>();
    this.items.forEach((item) => {
      if (item.area === area && item.kind === kind && item.type === type && item.classification) {
        set.add(item.classification);
      }
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  public getAllAreaNames(): string[] {
    const areas = new Set<string>();
    this.items.forEach((i) => {
      if (i.area) areas.add(i.area);
    });
    return Array.from(areas).sort((a, b) => a.localeCompare(b));
  }

  public getAllTypeNames(): string[] {
    const types = new Set<string>();
    this.items.forEach((i) => {
      if (i.type) types.add(i.type);
    });
    return Array.from(types).sort((a, b) => a.localeCompare(b));
  }

  public getAllClassificationNames(): string[] {
    const classes = new Set<string>();
    this.items.forEach((i) => {
      if (i.classification) classes.add(i.classification);
    });
    return Array.from(classes).sort((a, b) => a.localeCompare(b));
  }

  public getTypesForArea(area: string): string[] {
    const types = new Set<string>();
    this.items.forEach((i) => {
      if (i.area === area && i.type) types.add(i.type);
    });
    return Array.from(types).sort((a, b) => a.localeCompare(b));
  }

  public getClassificationsForAreaAndType(area?: string, type?: string): string[] {
    const classes = new Set<string>();
    this.items.forEach((i) => {
      if (area && i.area !== area) return;
      if (type && i.type !== type) return;
      if (i.classification) classes.add(i.classification);
    });
    return Array.from(classes).sort((a, b) => a.localeCompare(b));
  }

  public addTaxonomyItem(item: TaxonomyItem): void {
    if (!item.area || !item.kind || !item.type || !item.classification) {
      throw new Error('All taxonomy levels (area, kind, type, classification) are required.');
    }
    // Check for duplicate
    const exists = this.items.some(
      (i) =>
        i.area === item.area &&
        i.kind === item.kind &&
        i.type === item.type &&
        i.classification.trim().toLowerCase() === item.classification.trim().toLowerCase()
    );
    if (exists) {
      throw new Error(`Classification "${item.classification}" already exists under ${item.area} → ${item.kind} → ${item.type}.`);
    }

    const newItem: TaxonomyItem = {
      area: item.area.trim(),
      kind: item.kind.trim(),
      type: item.type.trim(),
      classification: item.classification.trim()
    };
    this.items.push(newItem);
    this.persistAndNotify();

    // Persist to PostgreSQL database table via Backend REST API
    if (typeof window !== 'undefined' && typeof fetch !== 'undefined') {
      fetch('/api/v1/admin/taxonomy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-organization-id': 'ORG-XYZ',
          'x-user-role': 'ROLE_SYSTEM_ADMIN'
        },
        body: JSON.stringify(newItem)
      }).then(async (res) => {
        if (res.ok) {
          const json = await res.json().catch(() => null);
          if (json?.data?.id) {
            newItem.id = String(json.data.id);
          }
        }
      }).catch(() => {});
    }
  }

  public updateTaxonomyItem(oldItem: TaxonomyItem, newItem: TaxonomyItem): void {
    const index = this.items.findIndex(
      (i) =>
        i.area === oldItem.area &&
        i.kind === oldItem.kind &&
        i.type === oldItem.type &&
        i.classification === oldItem.classification
    );

    if (index === -1) {
      throw new Error('Original taxonomy item not found to update.');
    }

    const updated: TaxonomyItem = {
      id: oldItem.id,
      area: newItem.area.trim(),
      kind: newItem.kind.trim(),
      type: newItem.type.trim(),
      classification: newItem.classification.trim()
    };
    this.items[index] = updated;
    this.persistAndNotify();

    // Persist update to PostgreSQL database
    if (oldItem.id && typeof window !== 'undefined' && typeof fetch !== 'undefined') {
      fetch(`/api/v1/admin/taxonomy/${oldItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-organization-id': 'ORG-XYZ',
          'x-user-role': 'ROLE_SYSTEM_ADMIN'
        },
        body: JSON.stringify(updated)
      }).catch(() => {});
    }
  }

  public deleteTaxonomyItem(item: TaxonomyItem): void {
    const target = this.items.find(
      (i) =>
        i.area === item.area &&
        i.kind === item.kind &&
        i.type === item.type &&
        i.classification === item.classification
    );
    const targetId = target?.id || item.id;

    const initialLen = this.items.length;
    this.items = this.items.filter(
      (i) =>
        !(
          i.area === item.area &&
          i.kind === item.kind &&
          i.type === item.type &&
          i.classification === item.classification
        )
    );

    if (this.items.length !== initialLen) {
      this.persistAndNotify();
    }

    // Persist soft-delete to PostgreSQL database
    if (targetId && typeof window !== 'undefined' && typeof fetch !== 'undefined') {
      fetch(`/api/v1/admin/taxonomy/${targetId}`, {
        method: 'DELETE',
        headers: {
          'x-organization-id': 'ORG-XYZ',
          'x-user-role': 'ROLE_SYSTEM_ADMIN'
        }
      }).catch(() => {});
    }
  }

  public importFromJSON(jsonString: string): { success: boolean; count: number; error?: string } {
    try {
      let raw = jsonString.trim();
      if (raw.endsWith(';')) raw = raw.slice(0, -1);
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return { success: false, count: 0, error: 'JSON root must be an array of taxonomy objects.' };
      }

      const validItems: TaxonomyItem[] = [];
      for (const entry of parsed) {
        if (entry && typeof entry === 'object' && entry.area && entry.kind && entry.type && entry.classification) {
          validItems.push({
            area: String(entry.area).trim(),
            kind: String(entry.kind).trim(),
            type: String(entry.type).trim(),
            classification: String(entry.classification).trim()
          });
        }
      }

      if (validItems.length === 0) {
        return { success: false, count: 0, error: 'No valid taxonomy entries found with {area, kind, type, classification}.' };
      }

      this.items = validItems;
      this.persistAndNotify();

      // Persist bulk array to PostgreSQL database table
      if (typeof window !== 'undefined' && typeof fetch !== 'undefined') {
        fetch('/api/v1/admin/taxonomy/import', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-organization-id': 'ORG-XYZ',
            'x-user-role': 'ROLE_SYSTEM_ADMIN'
          },
          body: JSON.stringify({ items: validItems })
        }).catch(() => {});
      }

      return { success: true, count: validItems.length };
    } catch (err: any) {
      return { success: false, count: 0, error: err?.message || 'Invalid JSON format' };
    }
  }

  public exportToJSON(): string {
    return JSON.stringify(this.items, null, 2);
  }
}

export const taxonomyService = new TaxonomyService();
